import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add LINE user token to requests
export const setAuthToken = (token: string) => {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

// Types
export interface Bridgelet {
  id: string;
  name: string;
  persona: {
    tone: 'friendly' | 'cautious' | 'professional';
    negotiationStyle: 'aggressive' | 'balanced' | 'conservative';
    riskTolerance: 'low' | 'medium' | 'high';
    language: 'th' | 'en';
  };
  stats: {
    dealsCompleted: number;
    successRate: number;
    avgNegotiationTime: number;
    totalSaved: number;
  };
  status: 'active' | 'inactive' | 'learning';
}

export interface Deal {
  id: string;
  type: 'buy' | 'sell';
  item: {
    name: string;
    category: string;
    imageUrl?: string;
  };
  price: {
    original?: number;
    current: number;
    saved?: number;
  };
  status: 'negotiating' | 'confirmed' | 'held' | 'completed' | 'disputed' | 'cancelled';
  counterparty: {
    name: string;
    trustScore: number;
  };
  timeline: {
    created: string;
    updated: string;
    deadline?: string;
  };
  aiAssisted: boolean;
}

// Add LINE user ID to requests
export const setLineUserId = (userId: string) => {
  api.defaults.headers.common['X-LINE-User-ID'] = userId;
};

// API Methods
export const bridgeletApi = {
  get: async (): Promise<Bridgelet> => {
    const { data } = await api.get('/liff/bridgelet');
    return data;
  },

  update: async (updates: Partial<Bridgelet['persona']>): Promise<Bridgelet> => {
    const { data } = await api.patch('/liff/bridgelet', updates);
    return data;
  },

  updateInstructions: async (customInstructions: string): Promise<{ success: boolean; bridgelet: Bridgelet; violations?: any[] }> => {
    const { data } = await api.patch('/liff/bridgelet/instructions', { customInstructions });
    return data;
  },
};

export const dealsApi = {
  list: async (): Promise<Deal[]> => {
    const { data } = await api.get('/liff/deals');
    return data;
  },

  get: async (id: string): Promise<Deal> => {
    const { data } = await api.get(`/liff/deals/${id}`);
    return data;
  },

  create: async (params: {
    type: 'buy' | 'sell';
    message: string;
  }): Promise<{ id: string; message: string }> => {
    const { data } = await api.post('/liff/deals', params);
    return data;
  },

  cancel: async (id: string): Promise<void> => {
    await api.post(`/liff/deals/${id}/cancel`);
  },

  confirm: async (id: string): Promise<void> => {
    await api.post(`/liff/deals/${id}/confirm`);
  },
};

export const userApi = {
  getProfile: async () => {
    const { data } = await api.get('/liff/profile');
    return data;
  },
};

export interface LoginResponse {
  message: string;
  user: {
    id: string;
    email: string | null;
    authType: string;
    verifiedLevel: string;
    createdAt: string;
  };
  accessToken: string;
  refreshToken: string;
}

export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const { data } = await api.post('/auth/login', { email, password });
    return data;
  },

  register: async (email: string, password: string): Promise<LoginResponse> => {
    const { data } = await api.post('/auth/register', { email, password });
    return data;
  },
};
